import { cva, type VariantProps } from "class-variance-authority";
import { type ComponentType, type ElementType, forwardRef } from "react";
import { twMerge } from "tailwind-merge";

const iconButtonVariants = cva(
	[
		"inline-flex items-center justify-center shrink-0",
		"border border-solid",
		"rounded-radius-component-specific-button",
		"transition-colors duration-200",
		"cursor-pointer",
		"disabled:cursor-not-allowed",
		"focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current",
	],
	{
		variants: {
			variant: {
				white: [],
				overlay: [],
				"ghost-primary": [],
				"ghost-secondary": [],
			},
			size: {
				xs: "size-5",
				sm: "size-6",
				md: "size-8",
				lg: "size-12",
			},
			darkBg: {
				true: "",
				false: "",
			},
		},
		compoundVariants: [
			{
				variant: "white",
				className: [
					"bg-button-solid-white-default-bg",
					"border-button-solid-white-default-border",
					"text-button-solid-white-default-label",
					"hover:bg-button-solid-white-hover-bg",
					"hover:border-button-solid-white-hover-border",
					"hover:text-button-solid-white-hover-label",
					"active:bg-button-solid-white-active-bg",
					"active:border-button-solid-white-active-border",
					"active:text-button-solid-white-active-label",
					"disabled:bg-button-solid-white-disabled-bg",
					"disabled:border-button-solid-white-disabled-border",
					"disabled:text-button-solid-white-disabled-label",
				],
			},
			{
				variant: "overlay",
				className: [
					"bg-button-overlay-default-bg",
					"border-button-overlay-default-border",
					"text-button-overlay-default-label",
					"hover:bg-button-overlay-hover-bg",
					"hover:border-button-overlay-hover-border",
					"hover:text-button-overlay-hover-label",
					"active:bg-button-overlay-active-bg",
					"active:border-button-overlay-active-border",
					"active:text-button-overlay-active-label",
					"disabled:bg-button-overlay-disabled-bg",
					"disabled:border-button-overlay-disabled-border",
					"disabled:text-button-overlay-disabled-label",
				],
			},
			{
				variant: "ghost-primary",
				className: [
					"bg-button-ghost-primary-default-bg",
					"border-button-ghost-primary-default-border",
					"text-button-ghost-primary-default-label",
					"hover:bg-button-ghost-primary-hover-bg",
					"hover:border-button-ghost-primary-hover-border",
					"hover:text-button-ghost-primary-hover-label",
					"active:bg-button-ghost-primary-active-bg",
					"active:border-button-ghost-primary-active-border",
					"active:text-button-ghost-primary-active-label",
					"disabled:bg-button-ghost-primary-disabled-bg",
					"disabled:border-button-ghost-primary-disabled-border",
					"disabled:text-button-ghost-primary-disabled-label",
				],
			},
			{
				variant: "ghost-secondary",
				className: [
					"bg-button-ghost-secondary-default-bg",
					"border-button-ghost-secondary-default-border",
					"text-button-ghost-secondary-default-label",
					"hover:bg-button-ghost-secondary-hover-bg",
					"hover:border-button-ghost-secondary-hover-border",
					"hover:text-button-ghost-secondary-hover-label",
					"active:bg-button-ghost-secondary-active-bg",
					"active:border-button-ghost-secondary-active-border",
					"active:text-button-ghost-secondary-active-label",
					"disabled:bg-button-ghost-secondary-disabled-bg",
					"disabled:border-button-ghost-secondary-disabled-border",
					"disabled:text-button-ghost-secondary-disabled-label",
				],
			},
			// darkBg overrides — variants whose default tokens use dark text/border
			// become invisible on a dark surface, mirroring Button.tsx.
			{
				variant: "white",
				darkBg: true,
				className: [
					"disabled:bg-neutral-transparent-white-15",
					"disabled:border-transparent",
					"disabled:text-neutral-transparent-white-40",
				],
			},
			{
				variant: "overlay",
				darkBg: true,
				className: [
					"bg-neutral-transparent-white-15",
					"border-transparent",
					"text-neutral-white-100",
					"hover:bg-neutral-transparent-white-25",
					"hover:border-transparent",
					"hover:text-neutral-white-100",
					"active:bg-neutral-transparent-white-40",
					"active:border-transparent",
					"active:text-neutral-white-100",
					"disabled:bg-neutral-transparent-white-15",
					"disabled:border-transparent",
					"disabled:text-neutral-transparent-white-40",
				],
			},
			{
				variant: "ghost-primary",
				darkBg: true,
				className: [
					"bg-transparent",
					"border-transparent",
					"text-neutral-white-100",
					"hover:bg-transparent",
					"hover:border-transparent",
					"hover:text-brand-sand-60",
					"active:bg-transparent",
					"active:border-transparent",
					"active:text-brand-sand-60",
					"disabled:bg-transparent",
					"disabled:border-transparent",
					"disabled:text-neutral-transparent-white-40",
				],
			},
			{
				variant: "ghost-secondary",
				darkBg: true,
				className: [
					"bg-transparent",
					"border-transparent",
					"text-brand-sand-60",
					"hover:bg-transparent",
					"hover:border-transparent",
					"hover:text-neutral-white-100",
					"active:bg-transparent",
					"active:border-transparent",
					"active:text-neutral-white-100",
					"disabled:bg-transparent",
					"disabled:border-transparent",
					"disabled:text-neutral-transparent-white-40",
				],
			},
		],
		defaultVariants: {
			variant: "ghost-primary",
			size: "md",
			darkBg: false,
		},
	},
);

const iconSizes = cva("shrink-0", {
	variants: {
		size: {
			xs: "size-3",
			sm: "size-3.5",
			md: "size-4",
			lg: "size-6",
		},
	},
	defaultVariants: {
		size: "md",
	},
});

type IconButtonVariants = VariantProps<typeof iconButtonVariants>;

type SharedProps = IconButtonVariants & {
	icon: ComponentType<{ className?: string }>;
	label: string;
	onClick?: () => void;
	disabled?: boolean;
	className?: string;
};

type IconButtonAsButton = SharedProps & {
	as?: "button";
	type?: "button" | "submit";
	href?: never;
};

type IconButtonAsLink = SharedProps & {
	as: ElementType;
	href: string;
	type?: never;
};

type Props = IconButtonAsButton | IconButtonAsLink;

export const IconButton = forwardRef<HTMLElement, Props>((props, ref) => {
	const { icon: Icon, label, size, variant, className, onClick, disabled, darkBg } = props;
	const classes = twMerge(iconButtonVariants({ size, variant, darkBg }), className);
	const content = <Icon className={iconSizes({ size })} />;

	if ("as" in props && props.as !== "button" && props.as != null) {
		const Component = props.as as ElementType;
		return (
			<Component
				onClick={onClick}
				className={classes}
				href={props.href}
				ref={ref}
				aria-label={label}
			>
				{content}
			</Component>
		);
	}

	return (
		<button
			type={("type" in props ? props.type : undefined) ?? "button"}
			onClick={onClick}
			className={classes}
			disabled={disabled}
			ref={ref as React.Ref<HTMLButtonElement>}
			aria-label={label}
		>
			{content}
		</button>
	);
});

IconButton.displayName = "IconButton";
